// A card that can be folded away, for record pages made of several groups of fields.
//
// Open or shut is remembered per key, so somebody who never looks at banking details stops seeing
// them, and somebody who lives in them does not have to keep opening the same box.
import { type ReactNode, useState } from "react";
import { Icon } from "./ui";

const KEY = "keel-section";

const remembered = (key: string, fallback: boolean) => {
  try {
    const v = localStorage.getItem(`${KEY}:${key}`);
    return v === null ? fallback : v === "1";
  } catch {
    return fallback;
  }
};

export function Section({
  title,
  /** Stable id for remembering whether it is open. Omit to always start from `defaultOpen`. */
  id,
  defaultOpen = true,
  count,
  action,
  children,
}: {
  title: string;
  id?: string;
  defaultOpen?: boolean;
  /** Shown beside the title, e.g. how many rows are inside. */
  count?: ReactNode;
  /** A button in the header; it does not toggle the section. */
  action?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(() => (id ? remembered(id, defaultOpen) : defaultOpen));
  const toggle = () => {
    setOpen((o) => {
      if (id) {
        try {
          localStorage.setItem(`${KEY}:${id}`, o ? "0" : "1");
        } catch {
          /* ignore */
        }
      }
      return !o;
    });
  };
  return (
    <section className={`card section ${open ? "" : "is-shut"}`}>
      <div className="section-head">
        <button type="button" className="section-toggle" onClick={toggle} aria-expanded={open}>
          <Icon name="chevron" size={15} />
          <h3>{title}</h3>
          {count !== undefined && count !== null && <span className="muted sm">{count}</span>}
        </button>
        {/* Acting on a shut section opens it: what the button adds or edits is drawn inside the
            body, so on a shut card "Add" used to do nothing anyone could see. */}
        {action && (
          <div className="actions" onClickCapture={() => !open && toggle()}>
            {action}
          </div>
        )}
      </div>
      {open && <div className="section-body">{children}</div>}
    </section>
  );
}

/**
 * One labelled value on a record page: text when you are reading, a control when you are editing.
 *
 * The point is that both states live in the same place. A card that shows less than its own edit
 * dialog makes people open the dialog to find out what they have.
 */
export function Row({
  label,
  value,
  editing,
  hint,
  children,
  wide,
}: {
  label: string;
  /** What to show when not editing. Falls back to an em dash. */
  value?: ReactNode;
  editing?: boolean;
  hint?: string;
  /** The control, rendered instead of `value` while editing. */
  children?: ReactNode;
  /** Put the control on its own line — for notes and addresses. */
  wide?: boolean;
}) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div className={`row-field ${wide ? "is-wide" : ""}`}>
      <span className="row-label">
        {label}
        {hint && <small>{hint}</small>}
      </span>
      <div className="row-value">
        {editing && children ? children : <span className={empty ? "muted" : ""}>{empty ? "—" : value}</span>}
      </div>
    </div>
  );
}

/**
 * Records listed in a card on a record page: contacts, ship-to addresses (ERP-136). The same row
 * everywhere — a title with its tags, what else there is to know on the lines under it, and the
 * row's own buttons at the edge — drawn like the label-and-value rows of the cards beside it,
 * rather than as a table squeezed into a side column.
 */
export function ItemList({ children }: { children: ReactNode }) {
  return <ul className="item-list">{children}</ul>;
}

export function Item({
  title,
  tags,
  actions,
  muted,
  children,
}: {
  title: ReactNode;
  /** Small labels after the title: `default`, `primary`, `retired`. */
  tags?: ReactNode;
  /** The row's buttons, e.g. edit and remove. */
  actions?: ReactNode;
  /** Retired or inactive: shown, but quieter. */
  muted?: boolean;
  /** Lines under the title. */
  children?: ReactNode;
}) {
  return (
    <li className={`item-row ${muted ? "is-muted" : ""}`}>
      <div className="item-main">
        <div className="item-title">
          <span className="strong">{title}</span>
          {tags}
        </div>
        {children && <div className="item-lines">{children}</div>}
      </div>
      {actions && <div className="item-actions">{actions}</div>}
    </li>
  );
}
